class SterlingShoppableVideo extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.video = this.querySelector('video');
    this.playToggle = this.querySelector('[data-video-toggle]');
    this.muteToggle = this.querySelector('[data-video-mute]');
    this.expandToggle = this.querySelector('[data-video-expand]');
    this.collapseToggle = this.querySelector('[data-video-collapse]');
    this.finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.hoverExpandTimer = null;
    this.hoverCollapseTimer = null;
    this.isPinned = false;
    this.dataset.state = 'collapsed';
    this.dataset.pinned = 'false';

    this.addEventListener('pointerenter', () => this.queueHoverExpand(), { signal });
    this.addEventListener('pointerleave', () => this.queueHoverCollapse(), { signal });
    this.expandToggle?.addEventListener('click', () => this.handleExpandActivation(), { signal });
    this.collapseToggle?.addEventListener('click', () => this.collapse(), { signal });
    this.playToggle?.addEventListener('click', () => this.togglePlayback(), { signal });
    this.muteToggle?.addEventListener('click', () => this.toggleMuted(), { signal });

    this.video?.addEventListener('play', () => this.syncControls(), { signal });
    this.video?.addEventListener('pause', () => this.syncControls(), { signal });
    this.video?.addEventListener('volumechange', () => this.syncControls(), { signal });

    if (this.video) {
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.setAttribute('muted', '');
      this.video.setAttribute('playsinline', '');
      this.syncControls();
      this.attemptAutoplay();
    }
  }

  disconnectedCallback() {
    this.clearHoverTimers();
    this.video?.pause();
    this.abortController?.abort();
  }

  clearHoverTimers() {
    window.clearTimeout(this.hoverExpandTimer);
    window.clearTimeout(this.hoverCollapseTimer);
    this.hoverExpandTimer = null;
    this.hoverCollapseTimer = null;
  }

  queueHoverExpand() {
    if (!this.finePointerQuery.matches || this.isPinned) return;
    window.clearTimeout(this.hoverCollapseTimer);
    this.hoverCollapseTimer = null;
    if (this.dataset.state === 'expanded') return;
    window.clearTimeout(this.hoverExpandTimer);
    this.hoverExpandTimer = window.setTimeout(() => {
      this.hoverExpandTimer = null;
      this.expand();
    }, 125);
  }

  queueHoverCollapse() {
    if (!this.finePointerQuery.matches || this.isPinned) return;
    window.clearTimeout(this.hoverExpandTimer);
    window.clearTimeout(this.hoverCollapseTimer);
    this.hoverExpandTimer = null;
    if (this.dataset.state === 'collapsed') return;
    this.hoverCollapseTimer = window.setTimeout(() => {
      this.hoverCollapseTimer = null;
      this.collapse({ restoreFocus: false });
    }, 200);
  }

  handleExpandActivation() {
    this.clearHoverTimers();
    if (this.finePointerQuery.matches) {
      this.pin();
      return;
    }
    this.toggleExpanded();
  }

  async attemptAutoplay() {
    if (!this.video || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    try {
      await this.video.play();
    } catch {
      this.syncControls();
    }
  }

  async togglePlayback() {
    if (!this.video) return;
    if (this.video.paused) {
      try {
        await this.video.play();
      } catch {
        this.syncControls();
      }
    } else {
      this.video.pause();
    }
  }

  toggleMuted() {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.syncControls();
  }

  toggleExpanded() {
    if (this.dataset.state === 'expanded') this.collapse();
    else this.expand();
  }

  expand() {
    this.dataset.state = 'expanded';
    this.expandToggle?.setAttribute('aria-expanded', 'true');
    this.expandToggle?.setAttribute(
      'aria-label',
      this.finePointerQuery.matches ? 'Keep shoppable video open' : 'Collapse shoppable video'
    );
    this.syncExpandedControl();
  }

  pin() {
    this.isPinned = true;
    this.dataset.pinned = 'true';
    this.expand();
    this.expandToggle?.setAttribute('aria-label', 'Shoppable video pinned open');
  }

  collapse({ restoreFocus = true } = {}) {
    this.clearHoverTimers();
    this.isPinned = false;
    this.dataset.state = 'collapsed';
    this.dataset.pinned = 'false';
    this.expandToggle?.setAttribute('aria-expanded', 'false');
    this.expandToggle?.setAttribute('aria-label', 'Expand shoppable video');
    this.syncExpandedControl();
    if (restoreFocus) this.expandToggle?.focus({ preventScroll: true });
  }

  syncControls() {
    if (!this.video) return;
    const isPlaying = !this.video.paused;
    const isMuted = this.video.muted;

    if (this.playToggle) {
      this.playToggle.setAttribute('aria-pressed', String(isPlaying));
      this.playToggle.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
      const playIcon = this.playToggle.querySelector('[data-video-icon-play]');
      const pauseIcon = this.playToggle.querySelector('[data-video-icon-pause]');
      if (playIcon) playIcon.hidden = isPlaying;
      if (pauseIcon) pauseIcon.hidden = !isPlaying;
    }

    if (this.muteToggle) {
      this.muteToggle.setAttribute('aria-pressed', String(isMuted));
      this.muteToggle.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
      const mutedIcon = this.muteToggle.querySelector('[data-video-icon-muted]');
      const unmutedIcon = this.muteToggle.querySelector('[data-video-icon-unmuted]');
      if (mutedIcon) mutedIcon.hidden = !isMuted;
      if (unmutedIcon) unmutedIcon.hidden = isMuted;
    }
  }

  syncExpandedControl() {
    const expanded = this.dataset.state === 'expanded';
    const expandIcon = this.expandToggle?.querySelector('[data-video-expand-icon]');
    const collapseIcon = this.expandToggle?.querySelector('[data-video-collapse-icon]');
    if (expandIcon) expandIcon.hidden = expanded;
    if (collapseIcon) collapseIcon.hidden = !expanded;
  }
}

if (!customElements.get('sterling-shoppable-video')) {
  customElements.define('sterling-shoppable-video', SterlingShoppableVideo);
}
